import { inductionNotificationPath } from './inductionNotifications';
import { tilNotificationPath } from './tilNotifications';
import { customerInviteNotificationPath } from './customerInviteNotifications';
import { staffWarningNotificationPath } from './staffWarningNotifications';
import { sensorNotificationPath } from './sensorNotifications';
import { deliveryNotificationPath } from './deliveryNotifications';
import { salesNotificationPath } from './salesNotifications';

type Data = Record<string, string | undefined> | undefined;

const enc = (v: string) => encodeURIComponent(v);

// WHS reminders and assignments: the page the thing lives on, and its own
// record where that page has one.
function whsPath(type: string, data: Data): string | null {
  if (!type.startsWith('whs_')) return null;
  if (data?.report_id) return `/hr/whs/reports/${enc(data.report_id)}`;
  if (data?.risk_assessment_id) return `/hr/whs/risk-assessments/${enc(data.risk_assessment_id)}`;
  if (data?.inspection_id) return `/hr/whs/inspections/${enc(data.inspection_id)}`;
  if (data?.action_id) return '/hr/whs/actions';
  if (data?.objective_id) return '/hr/whs/objectives';
  if (data?.management_review_id) return '/hr/whs/management-reviews';
  if (data?.training_id) return '/hr/whs/training';
  if (data?.emergency_plan_id) return '/hr/whs/emergency-plans';
  if (data?.document_id) return '/hr/whs/documents';
  if (type === 'whs_manager_nominated' || type === 'whs_role_nominated' || type === 'whs_appointment_expiry') {
    return '/hr/whs/settings';
  }
  return '/hr/whs';
}

/**
 * Where tapping a notification opens. The one table for the bell, its desktop
 * pop-ups and native pushes, so a type added on the server needs adding once.
 * Every type HHNodeServer's createNotification sends should land somewhere
 * specific; '/dashboard' is only for a type this does not know yet.
 */
export function notificationPath(type: string | undefined, data?: Data): string {
  if (!type) return '/dashboard';

  // Timesheets and leave
  if (type === 'timesheet_submitted' || type === 'leave_submitted' || type === 'missed_clock_declared'
    || type === 'timesheet_approval_reminder' || type === 'leave_released') return '/approvals';
  if (type.startsWith('timesheet_approved') || type.startsWith('timesheet_rejected')) return '/timesheet/history';
  if (type === 'timesheet_submit_nudge' || type === 'timesheet_reminder') return '/timesheet';
  if (type.startsWith('leave_approved') || type.startsWith('leave_rejected')) return '/timesheet';
  // Sent to the employee and HR alike; /timesheet shows the balances to both.
  if (type === 'leave_entitlement_changed') return '/timesheet';
  if (type === 'leave_balance_exceeded' || type === 'leave_accrual_failed') {
    return data?.userId ? `/hr/staff?tab=leave&user=${enc(data.userId)}` : '/hr/staff?tab=leave';
  }

  const helpers = [
    inductionNotificationPath(type, data),
    tilNotificationPath(type, data as Record<string, string>),
    customerInviteNotificationPath(type, data as Record<string, string>),
    staffWarningNotificationPath(type, data as Record<string, string>),
    deliveryNotificationPath(type, data as Record<string, string>),
    sensorNotificationPath(type, data as Record<string, string>),
    salesNotificationPath(type, data as Record<string, string>),
    whsPath(type, data),
  ];
  const found = helpers.find(p => p);
  if (found) return found;

  // Jobs
  if ((type === 'job_assigned' || type === 'job_completed') && data?.job_id) return `/jobs/${enc(data.job_id)}`;

  // Equipment. An insurance policy goes first: a one-machine policy carries
  // an equipment_id too.
  if (type.startsWith('equipment_insurance_') && data?.policy_id) return `/equipment/insurance/${enc(data.policy_id)}`;
  if (type.startsWith('equipment_') && data?.equipment_id) return `/equipment/${enc(data.equipment_id)}`;

  // Purchasing and accounts
  if (type === 'po_receipt_added' && data?.po_id) return `/purchase-orders/${enc(data.po_id)}`;
  if (type.startsWith('credit_note_request') && data?.request_id) return `/accounts/credit-note-requests?request=${enc(data.request_id)}`;

  // Nursery
  if (type === 'nursery_sourcing_request') return '/nursery/material?tab=requests';
  if (type === 'nursery_returnables_low') return data?.item && data.item !== 'stillage' ? `/nursery/stillages?item=${enc(data.item)}` : '/nursery/stillages';

  // Cultivar
  if (type === 'cultivar_evaluation_followup' && data?.cultivar_id) return `/cultivar/${enc(data.cultivar_id)}?tab=observations`;

  // Spray
  if (type.startsWith('spray_weather_') && data?.spray_id) return `/spray/${enc(data.spray_id)}`;
  if (type === 'spray_credential_override' && data?.spray_application_id) return `/spray/${enc(data.spray_application_id)}`;

  // Irrigation
  if (type === 'irrigation_rain_skip' && data?.controller_id) return `/irrigation/espnow/${enc(data.controller_id)}?tab=map`;
  if (type === 'irrigation_controller_alert' && data?.controller_id) return `/irrigation/espnow/${enc(data.controller_id)}`;

  if (type === 'pest_alert') {
    const q = new URLSearchParams();
    if (data?.farm_id) q.set('farm', data.farm_id);
    if (data?.pest_model_id) q.set('pest', data.pest_model_id);
    const qs = q.toString();
    return `/weather/pests${qs ? `?${qs}` : ''}`;
  }

  return '/dashboard';
}
